// Central definitions for status enums.
//
// Each enum is declared once here as:
//   - a `*_STATUSES` tuple   → feeds `z.enum(...)` in forms and the DB schema's type
//   - a `*_STATUS_OPTIONS`    → feeds `EnumSelect` (value + human label)
//
// Keeping both in one place means a new status is added in exactly one spot and
// every form, filter, and create dialog picks it up.

export interface EnumOption {
  value: string;
  label: string;
}

// ── Widget (the reference entity — see src/db/schema/widget.ts) ──
//
// `widget` is the template's one example entity. When you add a real entity,
// declare its statuses here the same way and import them in its form/filter.
export const WIDGET_STATUSES = ["draft", "active", "archived"] as const;
export type WidgetStatus = (typeof WIDGET_STATUSES)[number];
export const WIDGET_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

// ── Audit log ──
//
// `action` is the `<entity>.<verb>` string stored on every audit_log row, and
// `entityType` is the coarse kind it groups under. Both are validated against
// these allowlists in `recordAudit` (src/lib/audit.ts) and drive the filter
// dropdowns in the admin viewer. Add a new action here when you audit a new
// mutation — the helper rejects anything not listed (see .claude/rules/audit.md).

// ── Personal office door ──
export const PERSONAL_OFFICE_DOOR_STATUSES = ["open", "closed"] as const;
export type PersonalOfficeDoorStatus = (typeof PERSONAL_OFFICE_DOOR_STATUSES)[number];
export const PERSONAL_OFFICE_DOOR_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

export const AUDIT_ENTITY_TYPES = [
  "widget",
  "user",
  "auth",
  "personal_office",
  "vyllage_profile",
  "map_layout",
  "laptop_system",
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const AUDIT_ACTIONS = [
  // Reference-entity CRUD (src/examples/widget). Mirror this block per entity
  // you audit: `<entity>.created` / `.updated` / `.deleted`.
  "widget.created",
  "widget.updated",
  "widget.deleted",
  // Authentication events (captured via Better Auth hooks).
  "auth.login",
  "auth.login_failed",
  "auth.logout",
  "auth.signup",
  "auth.password_reset_requested",
  "auth.password_reset_completed",
  // Admin user management (Better Auth admin plugin endpoints).
  "user.created",
  "user.deleted",
  "user.role_changed",
  "user.banned",
  "user.unbanned",
  "user.password_reset",
  // Personal office pods (assignment + owner customization).
  "personal_office.assigned",
  "personal_office.unassigned",
  "personal_office.updated",
  "personal_office.claimed",
  // Vyllage avatar look + progression.
  "vyllage_profile.updated",
  "vyllage_profile.checked_in",
  "vyllage_profile.awarded",
  // Admin HQ map layout.
  "map_layout.draft_saved",
  "map_layout.published",
  // Desk laptop systems catalog + assignments.
  "laptop_system.created",
  "laptop_system.updated",
  "laptop_system.deleted",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const AUDIT_ACTION_OPTIONS: readonly EnumOption[] = AUDIT_ACTIONS.map((value) => ({
  value,
  label: value,
}));
